import { forwardRef, ReactNode } from "react";
import {
  Accordion as ChakraAccordion,
  AccordionItemProps as ChakraAccordionItemProps,
  useAccordionContext,
  useAccordionItemContext,
} from "@chakra-ui/react";
import { Spacing } from "@shared/theme/types";
import { resolveSpacingProps } from "@shared/utils/styles/spacing-props";
import { useAccordionIndex } from "./AccordionContext";

type AccordionItemStyleProps = {
  border?: string;
  borderColor?: string;
  borderTop?: string;
  borderBottom?: string;
  borderLeft?: string;
  borderRight?: string;
  borderRadius?: string;
  bg?: string;
  backgroundColor?: string;
  animation?: string;
  display?: string;
  width?: string;
  _first?: Record<string, unknown>;
  _last?: Record<string, unknown>;
  _only?: Record<string, unknown>;
} & Spacing;

export type AccordionItemProps = {
  children:
    | ReactNode
    | ((props: { isExpanded: boolean; isDisabled?: boolean }) => ReactNode);
  /**
   * Unique identifier for this item. Auto-generated if not provided.
   */
  value?: string;
  disabled?: boolean;
} & AccordionItemStyleProps &
  Omit<ChakraAccordionItemProps, "children" | "value" | "disabled">;

/**
 * Inner component that provides render prop with isExpanded state
 */
function AccordionItemInner({
  children,
  itemValue,
}: {
  children: AccordionItemProps["children"];
  itemValue: string;
}) {
  const rootContext = useAccordionContext();
  const itemContext = useAccordionItemContext();

  const expandedFromRoot = rootContext?.value?.includes(itemValue) ?? false;
  const expandedFromItem = itemContext?.open ?? false;
  const isExpanded = expandedFromRoot || expandedFromItem;
  const isDisabled = itemContext?.disabled ?? false;

  if (typeof children === "function") {
    return <>{children({ isExpanded, isDisabled })}</>;
  }
  return <>{children}</>;
}

const AccordionItem = forwardRef<HTMLDivElement, AccordionItemProps>(
  (props, ref) => {
    const {
      children,
      value,
      disabled,
      border,
      borderColor,
      borderTop,
      borderBottom,
      borderLeft,
      borderRight,
      borderRadius,
      bg,
      backgroundColor,
      // Spacing props - extracted for resolveSpacingProps
      margin,
      m,
      marginTop,
      mt,
      marginBottom,
      mb,
      marginLeft,
      ml,
      marginRight,
      mr,
      marginX,
      mx,
      marginY,
      my,
      padding,
      p,
      paddingTop,
      pt,
      paddingBottom,
      pb,
      paddingLeft,
      pl,
      paddingRight,
      pr,
      paddingX,
      px,
      paddingY,
      py,
      animation,
      display,
      width,
      ...rest
    } = props;

    // Get sequential index from Accordion context
    const autoIndex = useAccordionIndex();
    const itemValue = value ?? String(autoIndex);

    const hasIndividualBorders =
      borderTop !== undefined ||
      borderBottom !== undefined ||
      borderLeft !== undefined ||
      borderRight !== undefined;

    const getBorderStyle = () => {
      if (border !== undefined) return { border };
      if (!hasIndividualBorders) return { border: "none" };
      return {};
    };

    const spacingProps = resolveSpacingProps({
      margin,
      m,
      marginTop,
      mt,
      marginBottom,
      mb,
      marginLeft,
      ml,
      marginRight,
      mr,
      marginX,
      mx,
      marginY,
      my,
      padding,
      p,
      paddingTop,
      pt,
      paddingBottom,
      pb,
      paddingLeft,
      pl,
      paddingRight,
      pr,
      paddingX,
      px,
      paddingY,
      py,
    });

    const styleProps = {
      ...getBorderStyle(),
      ...(borderColor !== undefined ? { borderColor } : {}),
      ...(borderTop !== undefined ? { borderTop } : {}),
      ...(borderBottom !== undefined ? { borderBottom } : {}),
      ...(borderLeft !== undefined ? { borderLeft } : {}),
      ...(borderRight !== undefined ? { borderRight } : {}),
      ...(borderRadius !== undefined ? { borderRadius } : {}),
      ...(bg !== undefined || backgroundColor !== undefined
        ? { bg: bg ?? backgroundColor }
        : {}),
      ...spacingProps,
      ...(display !== undefined ? { display } : {}),
      ...(width !== undefined ? { width } : {}),
    };

    const cssProps = animation
      ? {
          css: {
            animation,
          },
        }
      : {};

    const itemProps = {
      ref,
      value: itemValue,
      disabled,
      ...styleProps,
      ...cssProps,
      ...rest,
    } as unknown as ChakraAccordionItemProps;

    return (
      <ChakraAccordion.Item {...itemProps}>
        <AccordionItemInner itemValue={itemValue}>
          {children}
        </AccordionItemInner>
      </ChakraAccordion.Item>
    );
  }
);

AccordionItem.displayName = "AccordionItem";

export default AccordionItem;
